/**
 * FinishTimeBody — thin orchestrator for the new Finish Time Entry screen.
 *
 * All UI primitives, cell renderers, column definitions, and sub-components
 * live in dedicated sibling files. This file owns:
 *  - Redux selectors & refs (synced during render for rapid-click correctness)
 *  - All stable handlers ([] deps, read from refs)
 *  - AG Grid context object (memoised, changes only on UI toggles)
 *  - The render layout (ControlsToolbar → AboutToFinishBoard → two AG Grids)
 */

import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import { unstable_batchedUpdates } from "react-dom";
import { AgGridReact } from "ag-grid-react";
import { Box, Grid, IconButton, TextField, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "../../../../actions/actions";
import axiosInstance from "../../../../axios/axiosInstance";
import { encrypt } from "../../../global/helperFunctions";

// ── Split module imports ─────────────────────────────────────────────────────
import {
  TIME_PENALTIES,
  MAX_WATCHLIST_SLOTS,
  pad2,
  isUnknownSail,
  sailMatches,
  sailPartialMatches,
  nextWatchOrder,
  toSeconds,
} from "./constants";
import { CountBadge, LabeledSwitch } from "./StyledComponents";
import { makeFinishedCols, makeUnfinishedCols } from "./columnDefs";
import ControlsToolbar from "./ControlsToolbar";
import AboutToFinishBoard from "./AboutToFinishBoard";

// ── Module-level constants (never re-created) ───────────────────────────────
const ROW_STYLE = { marginBottom: "3px" };
const getRowStyle = () => ROW_STYLE;
// Excel-like Tab chain per row. "place" exists only in place mode, "hr" only
// in time mode — the missing one is skipped, so one list serves both.
const INTERACTIVE_FIELDS = ["place", "hr", "dn", "del"];

// Unique id for phantom (unknown-sail) finished rows. A shared/empty id makes the
// id-keyed handlers (correct sail #, edit time, …) collide across phantoms.
const getGridRowId = (p) => p.data.id?.toString() || p.data.sailNum;
let _phantomSeq = 0;
const newPhantomId = () => `phantom-${++_phantomSeq}`;

// ─── Main Component ────────────────────────────────────────────────────────
function FinishTimeBody(props) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isCompact768 = useMediaQuery("(max-width:768px)");
  const compactTablet = useMediaQuery("(max-width:1024px)");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // <600px → grids stack

  // ── Redux selectors ────────────────────────────────────────────────────────
  const unfinishedEntrants = useSelector(
    (s) => s.initialFinishRaceEntrantsList.initialFinishRaceEntrantsList.data,
  );
  const completeFinishRaceEntrants = useSelector(
    (s) => s.completeFinishRaceEntrantsList.completeFinishRaceEntrantsList.data,
  );
  const aboutFinishRaceEntrants = useSelector(
    (s) => s.aboutFinishRaceEntrantsList.aboutFinishRaceEntrantsList.data,
  );
  // Count badge is updated imperatively (see AboutCountValue in
  // ControlsToolbar) — keeping it as state here re-rendered both grids on the
  // board's first keystroke. The store ref survives the badge's unmount in
  // manual mode, so switching back to real-time shows the real count (not 0/6).
  const aboutCountValueRef = useRef(null);
  const aboutCountStoreRef = useRef(0);
  const aboutAddRef = useRef(null);
  const handleAboutCount = useCallback((n) => {
    aboutCountStoreRef.current = n;
    aboutCountValueRef.current?.setCount(n);
    // "+" is disabled while the board is full.
    aboutAddRef.current?.setDisabled(n >= MAX_WATCHLIST_SLOTS);
  }, []);

  const mode = props.mode === "manual" ? "manual" : "real-time";

  // ── Refs synced during render (not in useEffect) ──────────────────────────
  const unfinishedRef = useRef(unfinishedEntrants);
  const finishedRef = useRef(completeFinishRaceEntrants);
  const aboutRef = useRef(aboutFinishRaceEntrants);
  const propsRef = useRef(props);
  unfinishedRef.current = unfinishedEntrants;
  finishedRef.current = completeFinishRaceEntrants;
  aboutRef.current = aboutFinishRaceEntrants;
  propsRef.current = props;

  // ── Local state ────────────────────────────────────────────────────────────
  const [retainHr, setRetainHr] = useState(false);
  const [retainDidNot, setRetainDidNot] = useState(false);
  // Retained values as state (drives the "Retain HR (14)" switch labels) with
  // ref mirrors below for the stable []-dep handlers.
  const [selectedHr, setSelectedHr] = useState("");
  // eslint-disable-next-line no-unused-vars -- read only by the commented-out
  // "Retain Did Not" switch label (see the Finished header below).
  const [selectedDidNot, setSelectedDidNot] = useState("");
  const snWithAlpha = props.snWithAlpha !== false;
  const onWater = !!props.onWater;
  const [unfinishedSearch, setUnfinishedSearch] = useState("");
  const [gridHeight, setGridHeight] = useState(100);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const finishedGridRef = useRef(null);
  // Expose the finished grid API to the parent (index.jsx) so the export
  // can read rows in their current sorted/filtered display order.
  useLayoutEffect(() => {
    if (props.finishedGridRef) props.finishedGridRef.current = finishedGridRef.current;
  });
  const unfinishedGridRef = useRef(null);
  const pendingTimeRef = useRef({});
  const lastSavedRef = useRef({});
  const boardRef = useRef(null);
  const gridWrapRef = useRef(null);
  const retainHrRef = useRef(retainHr);
  const retainDidNotRef = useRef(retainDidNot);
  const selectedHrRef = useRef("");
  const selectedDidNotRef = useRef("");
  retainHrRef.current = retainHr;
  retainDidNotRef.current = retainDidNot;

  // Toggling a retain switch in EITHER direction discards the retained value —
  // old-screen parity (updateHr/updateDidNot in app_components_finish-time.jsx).
  const updateRetainHr = useCallback((checked) => {
    setRetainHr(checked);
    setSelectedHr("");
    selectedHrRef.current = "";
  }, []);
  // eslint-disable-next-line no-unused-vars -- wired to the commented-out
  // "Retain Did Not" switch; kept so the switch can be restored as-is.
  const updateRetainDidNot = useCallback((checked) => {
    setRetainDidNot(checked);
    setSelectedDidNot("");
    selectedDidNotRef.current = "";
  }, []);

  // Changing Series/Event/Date/Session must reset both Retain toggles (and
  // their retained values) back to their default OFF state — otherwise they
  // kept the last-applied value across the switch. Keyed on the same
  // session/event/date identity the grids reload on; touches only the retain
  // state, nothing else.
  useEffect(() => {
    setRetainHr(false);
    setRetainDidNot(false);
    setSelectedHr("");
    setSelectedDidNot("");
    selectedHrRef.current = "";
    selectedDidNotRef.current = "";
  }, [props.session, props.event, props.raceSessionsDate]);

  // ── Derived values ─────────────────────────────────────────────────────────
  // Stable board prop — an inline .filter() made a new array every render,
  // defeating AboutToFinishBoard's React.memo (it re-rendered on every body
  // render, adding typing jank on tablets).
  const boardEntries = useMemo(
    () => aboutFinishRaceEntrants.filter((e) => !e.isAbandon),
    [aboutFinishRaceEntrants],
  );

  const filteredUnfinished = useMemo(() => {
    let list = onWater
      ? unfinishedEntrants.filter((e) => e?.onWater === 1)
      : unfinishedEntrants;
    // Manual mode surfaces about-entries in the unfinished grid (no about UI
    // there) — all series types, same as time mode.
    if (mode === "manual" && aboutFinishRaceEntrants.length > 0) {
      list = [...list, ...aboutFinishRaceEntrants];
    }
    return list;
  }, [onWater, unfinishedEntrants, mode, aboutFinishRaceEntrants]);

  // ── Dev-only invariant checks ──────────────────────────────────────────────
  // A sail must live in exactly one of unfinished/about/finished, and finished
  // ids must be unique. A warning here means the change you just made
  // desynced the lists — fix it now, not on a device three days later.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const seen = new Map();
    const check = (list, name) => {
      (list || []).forEach((r) => {
        const k = r.sailNum?.trim().toLowerCase();
        if (!k) return;
        if (seen.has(k) && seen.get(k) !== name) {
          console.warn(
            `[FinishTimes] INVARIANT: sail "${r.sailNum}" is in both "${seen.get(k)}" and "${name}"`,
          );
        }
        seen.set(k, name);
      });
    };
    check(unfinishedEntrants, "unfinished");
    check(aboutFinishRaceEntrants, "about-to-finish");
    check(completeFinishRaceEntrants, "finished");
    const ids = (completeFinishRaceEntrants || []).map((r) => r.id);
    if (new Set(ids).size !== ids.length) {
      console.warn("[FinishTimes] INVARIANT: duplicate row id in finished list");
    }
  }, [unfinishedEntrants, aboutFinishRaceEntrants, completeFinishRaceEntrants]);

  const finishedColDefs = useMemo(
    () =>
      makeFinishedCols(
        props.caseOTBEntrant,
        props.overnight,
        props.place,
        isTablet,
      ),
    [props.caseOTBEntrant, props.overnight, props.place, isTablet],
  );
  const unfinishedColDefs = useMemo(
    () => makeUnfinishedCols(props.caseOTBEntrant, mode, isTablet),
    [props.caseOTBEntrant, mode, isTablet],
  );

  // ── Tab navigation (finished grid) ─────────────────────────────────────────
  const tabToNextFinishedCell = useCallback((params) => {
    const allCols = params.api.getAllDisplayedColumns();
    const prevColId = params.previousCellPosition.column.getColId();
    const prevRow = params.previousCellPosition.rowIndex;
    const idx = INTERACTIVE_FIELDS.indexOf(prevColId);

    if (!params.backwards) {
      for (let i = idx + 1; i < INTERACTIVE_FIELDS.length; i++) {
        const col = allCols.find((c) => c.getColId() === INTERACTIVE_FIELDS[i]);
        if (col) return { rowIndex: prevRow, column: col };
      }
      const nextRow = prevRow + 1;
      if (!params.api.getDisplayedRowAtIndex(nextRow)) return null;
      const firstCol = allCols.find(
        (c) => c.getColId() === INTERACTIVE_FIELDS[0],
      );
      if (firstCol) return { rowIndex: nextRow, column: firstCol };
    } else {
      for (let i = idx - 1; i >= 0; i--) {
        const col = allCols.find((c) => c.getColId() === INTERACTIVE_FIELDS[i]);
        if (col) return { rowIndex: prevRow, column: col };
      }
      const prevRowIdx = prevRow - 1;
      if (!params.api.getDisplayedRowAtIndex(prevRowIdx)) return null;
      const lastCol = allCols.find(
        (c) =>
          c.getColId() === INTERACTIVE_FIELDS[INTERACTIVE_FIELDS.length - 1],
      );
      if (lastCol) return { rowIndex: prevRowIdx, column: lastCol };
    }
    return null;
  }, []);

  const onFinishedCellFocused = useCallback((params) => {
    const colId =
      typeof params.column === "string"
        ? params.column
        : params.column?.getColId?.();
    const rowIdx = params.rowIndex;
    if (rowIdx == null || rowIdx < 0 || !INTERACTIVE_FIELDS.includes(colId))
      return;
    requestAnimationFrame(() => {
      const rowEl = document.querySelector(
        `.nft-finished-grid .ag-row[row-index="${rowIdx}"]`,
      );
      if (!rowEl) return;
      if (colId === "place")
        rowEl.querySelector(".nft-place-input input")?.focus();
      if (colId === "hr") rowEl.querySelector(".nft-time-btn")?.focus();
      if (colId === "dn") rowEl.querySelector(".MuiSelect-select")?.focus();
      if (colId === "del") rowEl.querySelector(".nft-delete-btn")?.focus();
    });
  }, []);

   useEffect(() => {
    unfinishedGridRef.current?.api?.onFilterChanged();
    finishedGridRef.current?.api?.onFilterChanged();
  }, [unfinishedSearch]);

//   // Preserve "About to Finish" list across mode switches without useEffect.
//   // Snapshot the list while in real-time; if we re-enter real-time and redux
//   // has lost it, restore from the snapshot synchronously.
//  const aboutBackupRef = useRef([]);
//   const prevModeRef = useRef(mode);
//   if (mode === "real-time") {
//     if ((aboutFinishRaceEntrants?.length ?? 0) > 0) {
//       aboutBackupRef.current = aboutFinishRaceEntrants;
//     } else if (
//       prevModeRef.current !== "real-time" &&
//       aboutBackupRef.current.length > 0
//     ) {
//       // Just switched back to real-time and redux is empty: restore.
//       Promise.resolve().then(() => {
//         dispatch(actions.updateAboutFinishRaceEntrants(aboutBackupRef.current));
//       });
//     }
//   } else if ((aboutFinishRaceEntrants?.length ?? 0) > 0) {
//     aboutBackupRef.current = aboutFinishRaceEntrants;
//   }
//   prevModeRef.current = mode;

  // ── Fit grids to remaining viewport height ──────────────────────────────────
  useLayoutEffect(() => {
    const measure = () => {
      const el = gridWrapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // Use the VISIBLE viewport height. window.innerHeight is the layout
      const vh = window.visualViewport?.height ?? window.innerHeight;
      setGridHeight(Math.max(300, Math.round(vh - top - 35)));
    };
    const isTyping = () => {
      const ae = document.activeElement;
      return (
        !!ae &&
        (ae.tagName === "INPUT" ||
          ae.tagName === "TEXTAREA" ||
          ae.isContentEditable)
      );
    };
    const onResize = () => {
      if (!isTyping()) measure();
    };
    // Devices report stale dimensions right at an orientation flip → settle.
    let settle;
    const onOrient = () => {
      requestAnimationFrame(onResize);
      clearTimeout(settle);
      settle = setTimeout(onResize, 350);
    };

    measure();
    const raf = requestAnimationFrame(measure); // catch late layout (fonts / grid init)
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onOrient);
    // Re-fit when the browser toolbar shows/hides (changes the visible height).
    // Guarded by isTyping(), so the keyboard shrinking visualViewport never
    // re-measures → the iOS keyboard white-gap fix stays intact.
    window.visualViewport?.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onOrient);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, [mode, isMobile, compactTablet, props.isFullscreen]);

  // ── Sidebar-toggle smoothness ───────────────────────────────────────────────
  // Toggling the app sidebar changes the main content width, which resizes the
  // two AG grids; with heavy MUI cell renderers (Select/Buttons per row) that
  // per-frame reflow is very laggy. DefaultLayout fires "ty-sidebar-toggle"
  // synchronously the instant a toggle is requested — BEFORE React re-renders
  // the drawer and the browser reflows — so here we pin each grid to its current
  // pixel width (AG Grid then sees a constant width → no per-frame reflow), then
  // release + re-fit once after the animation settles.
  useEffect(() => {
    const wraps = () => [
      document.querySelector(".nft-finished-grid"),
      document.querySelector(".nft-unfinished-grid"),
    ];
    let releaseTimer = null;
    let pinned = false;
    const onToggle = () => {
      if (!pinned) {
        wraps().forEach((el) => {
          if (el) el.style.width = el.offsetWidth + "px";
        });
        pinned = true;
      }
      clearTimeout(releaseTimer);
      releaseTimer = setTimeout(() => {
        wraps().forEach((el) => {
          if (el) el.style.width = "";
        });
        pinned = false;
        window.dispatchEvent(new Event("resize")); // one re-fit + height re-measure
      }, 320); // covers the ~225ms drawer transition with margin
    };
    window.addEventListener("ty-sidebar-toggle", onToggle);
    return () => {
      window.removeEventListener("ty-sidebar-toggle", onToggle);
      clearTimeout(releaseTimer);
    };
  }, []);

  // ── Stable handlers ([] deps, read from refs) ──────────────────────────────
  const buildPayload = useCallback((row) => {
    const p = propsRef.current;
    return {
      raceSessionId: p.session,
      // Always send the real entrant id when the row has one (a backend entrant),
      // even while flagged for review (editable). A phantom/unknown-sail row has
      // no sessionEntrantId and only a CLIENT id (phantom-N) — never send that;
      // send "" so the backend treats it as a new record (matches the old screen,
      // which blanked id to "" for unknown-sail rows).
      sessionEntrantId:
        row.sessionEntrantId || (row.editable || isUnknownSail(row) ? "" : row.id),
      // Place mode (old-screen parity): send the user-typed number, or "" when
      // only a DN is set — never an auto-index. Time mode keeps the insertion
      // index (matches old time-mode saves).
      place: p.place
        ? row.place === "" || row.place == null
          ? ""
          : Number(row.place)
        : row.place || 0,
      sailNum: row.sailNum,
      hr: pad2(row.hr),
      mm: pad2(row.min),
      ss: pad2(row.sec),
      date: p.raceSessionsDate || "",
      finishDate: row.finishDate || "",
      dn: row.dn || "",
      fleet: row.fleetName || "",
      oldSailNum: row.oldSailNum || null,
      ...(p.caseOTBEntrant ? { class: row.class || "" } : {}),
    };
  }, []);

  const lastSailNumByIdRef = useRef({});
  const saveRow = useCallback((payload) => {
    // ... inside your save function, where `payload` is built for a given row:
    const rowId = payload.id; // or whatever uniquely identifies the row
    const key = payload.sailNum;
    const previousSailNum = lastSailNumByIdRef.current[rowId];
    if (previousSailNum && previousSailNum !== key) {
      // sailNum changed for this row — remove the stale fingerprint
      delete lastSavedRef.current[previousSailNum];
    }
    lastSailNumByIdRef.current[rowId] = key;
    // Fingerprint must cover EVERY user-editable payload field — place and
    // finishDate were missing, so a place-only or date-only edit produced the
    // same fingerprint and the POST was silently skipped (place/overnight
    // edits never persisted).
    const fingerprint = `${payload.hr}|${payload.mm}|${payload.ss}|${payload.dn}|${payload.place}|${payload.finishDate}`;
    console.log("sailNum", key);
    console.log("saveRow", lastSavedRef.current[key], fingerprint);
    if (lastSavedRef.current[key] === fingerprint) return;
    lastSavedRef.current[key] = fingerprint;

    axiosInstance
      .post("api/clubs/FinishTime", payload)
      .then((res) => {
        if (res.data.success) {
          const apiEditable =
            res.data?.data?.editable === true || res.data?.data?.editable === 1;
          // Only touch state when the server's verdict actually differs — a
          // full-array dispatch re-renders both grids, and doing it after
          // EVERY save stalled the UI (visible as the header clock skipping
          // a second right after a capture).
          const target = finishedRef.current.find(
            (r) => r.sailNum === payload.sailNum,
          );
          console.log("target", target)
          const clientEditable =
            target?.editable === true || target?.editable === 1;
          if (target && clientEditable !== apiEditable) {
            const updated = finishedRef.current.map((r) =>
              r.sailNum === payload.sailNum
                ? { ...r, editable: apiEditable }
                : r,
            );
            finishedRef.current = updated;
            dispatch(actions.updateCompleteFinishRaceEntrants(updated));
          }
        }
      })
      .catch((err) => {
        const target = finishedRef.current.find(
          (r) => r.sailNum === payload.sailNum,
        );
        if (target && !(target.editable === true || target.editable === 1)) {
          const updated = finishedRef.current.map((r) =>
            r.sailNum === payload.sailNum ? { ...r, editable: true } : r,
          );
          finishedRef.current = updated;
          dispatch(actions.updateCompleteFinishRaceEntrants(updated));
        }
        propsRef.current.activateSnackbarTab?.(
          err.response?.data?.message || err.message || "Unknown error",
          "error",
          true,
        );
        delete lastSavedRef.current[payload.sailNum];
      });
  }, []);

  const handleTimeBlur = useCallback((rowId, field, paddedValue, isClamped) => {
    const existing = finishedRef.current.find((r) => r.id === rowId);
    const sameValue = existing && String(existing[field]).padStart(2, "0") === paddedValue;
    const flagKey =
      field === "hr" ? "errorHR" : field === "min" ? "errorMin" : "errorSec";
    const flagStale = existing && !!existing[flagKey] !== !!isClamped;
    // Skip if value unchanged, valid, and the flag already agrees (e.g. user
    // tabbed through without editing — don't clear server-set editable state)
    if (sameValue && !isClamped && !flagStale) return;

    // Capture the retained HR only while the toggle is ON; typing "00" clears
    // it (old-screen parity — About-to-Finish HR valueSetter).
    if (field === "hr" && retainHrRef.current) {
      const v = paddedValue === "00" ? "" : paddedValue;
      selectedHrRef.current = v;
      setSelectedHr(v);
    }
    const updated = finishedRef.current.map((r) => {
      if (r.id !== rowId) return r;
      const next = { ...r, [field]: paddedValue };
      // store per-row clamped/error flags when provided
      if (typeof isClamped !== "undefined") {
        if (field === "hr") next.errorHR = !!isClamped;
        if (field === "min") next.errorMin = !!isClamped;
        if (field === "sec") next.errorSec = !!isClamped;
      }
      const hr  = field === "hr"  ? paddedValue : next.hr;
      const min = field === "min" ? paddedValue : next.min;
      const sec = field === "sec" ? paddedValue : next.sec;
      if (hr != null && min != null && sec != null) {
        // OPTIMISTIC verdict — same rules as handleDateChange (fleet start +
        // overnight date). Blanket-clearing to false here flashed a
        // STILL-INVALID row white until the server re-flagged it red, and
        // that late flip redrew the row and killed the focus that had just
        // Tab-moved to the next cell. The server's response still overrides
        // via saveRow's diff-guard if it disagrees.
        const p = propsRef.current;
        const sessionDate = p.raceSessionsDate || "";
        const nonTimeDn = !!(next.dn && !TIME_PENALTIES.includes(next.dn));
        let verdict = false;
        if (!nonTimeDn && !next.isAbandon && !isUnknownSail(next)) {
          const fd = next.finishDate || "";
          if (p.overnight && fd && sessionDate && fd < sessionDate) {
            verdict = true; // finished before the race day → invalid
          } else if (p.overnight && fd && sessionDate && fd > sessionDate) {
            verdict = false; // later day — time-of-day can't be before start
          } else {
            const fleet = (p.fleetDetails || []).find(
              (f) => f.fleet === next.fleetName,
            );
            const startSec = fleet
              ? toSeconds(fleet.hr, fleet.min, fleet.sec)
              : 0;
            verdict = startSec > 0 && toSeconds(hr, min, sec) < startSec;
          }
        }
        next.editable = verdict;
      }
      return next;
    });
    finishedRef.current = updated;
    dispatch(actions.updateCompleteFinishRaceEntrants(updated));

    const pending = pendingTimeRef.current;
    if (!pending[rowId]) pending[rowId] = {};
    pending[rowId][field] = paddedValue;
    clearTimeout(pending[rowId].timer);
    pending[rowId].timer = setTimeout(() => {
      const row = finishedRef.current.find((r) => r.id === rowId);
      if (row) {
        const payload = buildPayload(row);
        payload.oldSailNum = null; // time edits never rename — null so backend looks up by sailNum/sessionEntrantId
        saveRow(payload);
      }
      delete pending[rowId];
    }, 30);
  }, []);

  const handlePenaltyChange = useCallback((rowId, value) => {
    const existing = finishedRef.current.find((r) => r.id === rowId);
    if (existing && (existing.dn || "") === (value || "")) return;

    const isPlaceMode = !!propsRef.current.place;
    // Old-screen rule (place mode): the DN can't be cleared while the place is
    // empty — a finished row must always carry place OR DN. The Select is
    // controlled from row data, so returning without a dispatch keeps it
    // showing the existing penalty.
    if (isPlaceMode && !value && existing && !existing.place) {
      propsRef.current.activateSnackbarTab?.(
        "Please define either place or DN.",
        "error",
        true,
      );
      return;
    }

    // Capture the retained penalty only while the toggle is ON (old-screen
    // parity — About-to-Finish DN valueSetter).
    if (retainDidNotRef.current) {
      selectedDidNotRef.current = value || "";
      setSelectedDidNot(value || "");
    }

    const updated = finishedRef.current.map((r) => {
      if (r.id !== rowId) return r;
      const next = { ...r, dn: value };
      if (value && !TIME_PENALTIES.includes(value)) {
        next.hr = "00";
        next.min = "00";
        next.sec = "00";
        next.errorHR = false;
        next.errorMin = false;
        next.errorSec = false;
        // Non-time penalty (DNC, …) → no finish date either (matches old screen)
        next.finishDate = "";
      }
      // Place mode: a DN completes a place-less row (place "" + DN is a valid
      // old-screen result) — clear the review highlight. Place itself is
      // deliberately kept (old screen preserves place when a penalty is set).
      if (isPlaceMode && value) next.editable = false;
      return next;
    });
    finishedRef.current = updated;
    dispatch(actions.updateCompleteFinishRaceEntrants(updated));
    const row = updated.find((r) => r.id === rowId);
    if (row) {
      const payload = buildPayload(row);
      payload.oldSailNum = null; // penalty edits never rename — null so backend looks up by sailNum/sessionEntrantId
      saveRow(payload);
    }
  }, []);

  // Scroll the unfinished grid viewport to the very BOTTOM (requirement: any
  // row returned to Unfinished → the grid shows its bottom, where appended
  // rows land). Fired at three delays to cover any React/AG Grid flush
  // timing, and finished with a raw DOM scroll so it cannot silently no-op
  // (the row-node lookup approach failed when the node wasn't created yet or
  // an active column sort placed the row elsewhere).
  const scrollUnfinishedToBottom = useCallback(() => {
    const scroll = () => {
      const api = unfinishedGridRef.current?.api;
      const count = api?.getDisplayedRowCount ? api.getDisplayedRowCount() : 0;
      if (count > 0) api.ensureIndexVisible(count - 1, "bottom");
      const viewport = document.querySelector(
        ".nft-unfinished-grid .ag-body-viewport",
      );
      if (viewport) viewport.scrollTop = viewport.scrollHeight;
    };
    requestAnimationFrame(scroll);
    setTimeout(scroll, 150);
    setTimeout(scroll, 400);
  }, []);

  const handleDeleteFinished = useCallback((data) => {
    if (!navigator.onLine) {
      propsRef.current.activateSnackbarTab?.("Network error", "error", true);
      return;
    }
    const sailLower = data.sailNum?.toLowerCase();
    // Double-fire guard: refs update synchronously, so a second call for an
    // already-deleted row bails here. (The grids render from Redux rowData
    // only — no optimistic applyTransaction — so the dual-update desync that
    // produced ghost/empty rows can't happen.)
    if (!finishedRef.current.some((r) => r.id === data.id)) return;

    // Unknown sail# rows have no matching boat — they cannot be returned to the
    // unfinished list. Just drop them from the finished grid and the server.
    const unknownSail = isUnknownSail(data);

    const restored = unknownSail
      ? null
      : {
          ...data,
          hr: "00",
          min: "00",
          sec: "00",
          dn: "",
          position: "",
          place: "",
          finishDate: "",
          editable: false,
          errorHR: false,
          errorMin: false,
          errorSec: false,
        };

    // Old-screen parity: don't renumber every row — only decrement the
    // positions of boats that finished AFTER the deleted one, closing the
    // gap it leaves. Boats ahead of it keep their exact numbers.
    const deletedPosition = Number(data.position) || 0;
    const reNumbered = finishedRef.current
      .filter((r) => r.sailNum?.toLowerCase() !== sailLower)
      .map((r) => {
        const pos = Number(r.position) || 0;
        return pos > deletedPosition ? { ...r, position: pos - 1 } : r;
      });
    const newUnfinished = restored
      ? [...unfinishedRef.current, restored]
      : unfinishedRef.current;
    finishedRef.current = reNumbered;
    unfinishedRef.current = newUnfinished;

    requestAnimationFrame(() => {
      unstable_batchedUpdates(() => {
        dispatch(actions.updateCompleteFinishRaceEntrants(reNumbered));
        if (restored) {
          dispatch(actions.updateInitialFinishRaceEntrants(newUnfinished));
        }
      });
      // Show the grid bottom where the returned boat lands.
      if (restored) scrollUnfinishedToBottom();
    });
    axiosInstance
      .post("api/clubs/FinishTime/delete", buildPayload(data))
      .catch(console.error);
  }, []);

  const handleUnfinishedClick = useCallback((data) => {
    if (data?.isAbandon) return; // abandoned entrants cannot be moved (old screen parity)
    if (!navigator.onLine) {
      propsRef.current.activateSnackbarTab?.("Network error", "error", true);
      return;
    }
    const sailLower = data.sailNum?.toLowerCase();
    const finished = finishedRef.current;
    // Double-fire guard via the synchronously-updated refs. In manual mode the
    // unfinished grid also shows About-to-Finish entries, so check both lists.
    if (
      !unfinishedRef.current.some(
        (r) => r.sailNum?.toLowerCase() === sailLower,
      ) &&
      !aboutRef.current.some((r) => r.sailNum?.toLowerCase() === sailLower)
    )
      return;

    const isPlaceMode = !!propsRef.current.place;
    // Retain Did Not: the retained penalty or nothing — the old screen has no
    // fallback to another row's dn.
    const dn =
      retainDidNotRef.current && selectedDidNotRef.current
        ? selectedDidNotRef.current
        : "";

    let hr = "00",
      min = "00",
      sec = "00";
    let editable = false;
    const dnIsNonTime = dn && !TIME_PENALTIES.includes(dn);
    // Manual place: the row lands COMPLETE — with its carried Pos (an About
    // card surfaced in this grid), or auto-assigned max(finished places)+1,
    // same default as the real-time captures. A retained DN without a place
    // stays place "" + DN (old-screen rule). PLACE remains editable in the
    // grid afterwards.
    const carriedPlace = isPlaceMode
      ? Number(data.place) > 0
        ? Number(data.place)
        : dn
          ? ""
          : nextDefaultPlace()
      : "";
    if (isPlaceMode) {
      editable = false;
    } else if (dnIsNonTime) {
      // Non-time penalty (AVG, DNC, …): 00:00:00 is a complete, valid
      // result — no review highlight. A retained HR is suppressed here
      // (old isDefaultHrAllowed rule: non-time DN wins, hr stays "00").
    } else if (retainHrRef.current && selectedHrRef.current) {
      // Retained HR applied (old-screen rule): the row is NOT flagged for
      // review — the server's save response still decides editable. No
      // fallback to another row's hr; only the explicitly retained value.
      hr = selectedHrRef.current;
    } else {
      // Normal move: 00:00:00 is a placeholder needing a real finish time →
      // flag for review (row highlights, blocks Process Results until corrected).
      // Overnight: start white; the server verdict will turn it red if needed —
      // avoids the red→white flash caused by the server clearing editable on add.
      editable = !propsRef.current.overnight;
    }
    const newEntry = {
      ...data,
      hr,
      min,
      sec,
      dn,
      position: nextPosition(),
      place: isPlaceMode ? carriedPlace : nextPosition(),
      editable,
      // Overnight: default the finish date to today (old screen parity);
      // non-time penalty rows carry no date; non-overnight/place sends "".
      finishDate:
        dnIsNonTime || isPlaceMode
          ? ""
          : propsRef.current.overnight
            ? data.finishDate || new Date().toISOString().split("T")[0]
            : "",
    };
    const remainingUnfinished = unfinishedRef.current.filter(
      (r) => r.sailNum?.toLowerCase() !== sailLower,
    );
     const remainingAbout = aboutRef.current.filter(
      (r) => r.sailNum?.toLowerCase() !== sailLower,
    );
    const newFinished = [newEntry, ...finished];
    unfinishedRef.current = remainingUnfinished;
    aboutRef.current = remainingAbout;
    finishedRef.current = newFinished;

    requestAnimationFrame(() => {
      unstable_batchedUpdates(() => {
        dispatch(actions.updateInitialFinishRaceEntrants(remainingUnfinished));
        dispatch(actions.updateAboutFinishRaceEntrants(remainingAbout));
        dispatch(actions.updateCompleteFinishRaceEntrants(newFinished));
      });
      // The dispatches flushed synchronously above → the new top row exists in
      // the grid now; scroll it into view.
      finishedGridRef.current?.api?.ensureIndexVisible(0, "top");
    });
    // Persist the move (was only for non-time penalties) → row appears as a
    // finish (00:00:00 placeholder in time mode; auto/carried place or DN in
    // place mode) in the backend; re-saved on any later edit.
    saveRow(buildPayload(newEntry));
  }, []);

  const handleSailNumSave = useCallback((rowId, newSailNum) => {
    const current = finishedRef.current;
    const row = current.find((r) => r.id === rowId);
    if (!row) return;
    // Cleared to empty → error toast + revert (old-screen parity; the
    // renderer keeps showing the previous value because nothing changes here).
    if (!(newSailNum || "").trim()) {
      propsRef.current.activateSnackbarTab?.(
        "Please define sail number",
        "error",
        true,
      );
      return;
    }
    // SN Alpha rule (old screen): toggle ON → exact full-sail match only;
    // OFF → letters stripped from both sides before comparing.
    const alpha = propsRef.current.snWithAlpha !== false;

    // Duplicate: ONLY another FINISHED row counts — a sail sitting in About
    // to Finish (card or typed box) is NOT a duplicate; it gets FETCHED from
    // the board below instead. "already added!" fires solely when the sail is
    // truly in the finished grid already.
    const dup = current.some(
      (r) => r.id !== rowId && sailMatches(r.sailNum, newSailNum, alpha),
    );
    if (dup) {
      propsRef.current.activateSnackbarTab?.(
        `Sail Number ${newSailNum} already added!`,
        "error",
        true,
      );
      return;
    }

    const matchEntrant = unfinishedRef.current.find(
      (r) => !r.isAbandon && sailMatches(r.sailNum, newSailNum, alpha),
    );
    if (matchEntrant) {
      const movedEntry = {
        ...matchEntrant,
        hr: row.hr,
        min: row.min,
        sec: row.sec,
        dn: row.dn || "",
        finishDate: row.finishDate || "",
        position: row.position,
        place: row.place,
        editable: false,
        oldSailNum: "",
      };
      const remainingUnfinished = unfinishedRef.current.filter(
        (r) => r.id !== matchEntrant.id,
      );
      const newFinished = current.map((r) => (r.id === rowId ? movedEntry : r));
      unfinishedRef.current = remainingUnfinished;
      finishedRef.current = newFinished;
      requestAnimationFrame(() => {
        unstable_batchedUpdates(() => {
          dispatch(
            actions.updateInitialFinishRaceEntrants(remainingUnfinished),
          );
          dispatch(actions.updateCompleteFinishRaceEntrants(newFinished));
        });
      });
      saveRow(buildPayload(movedEntry));
      return;
    }

    // Check About to Finish list as second fallback. Several cards can share
    // the sail → fetch the LAST-added one.
    const aboutMatches = aboutRef.current.filter(
      (r) => !r.isAbandon && sailMatches(r.sailNum, newSailNum, alpha),
    );
    const matchAbout = aboutMatches[aboutMatches.length - 1];
    if (matchAbout) {
      const movedEntry = {
        ...matchAbout,
        hr: row.hr,
        min: row.min,
        sec: row.sec,
        dn: row.dn || "",
        finishDate: row.finishDate || "",
        position: row.position,
        place: row.place,
        editable: false,
        oldSailNum: row.sailNum || "",
      };
      const remainingAbout = aboutRef.current.filter(
        (r) => r.id !== matchAbout.id,
      );
      const newFinished = current.map((r) => (r.id === rowId ? movedEntry : r));
      aboutRef.current = remainingAbout;
      finishedRef.current = newFinished;
      requestAnimationFrame(() => {
        unstable_batchedUpdates(() => {
          dispatch(actions.updateAboutFinishRaceEntrants(remainingAbout));
          dispatch(actions.updateCompleteFinishRaceEntrants(newFinished));
        });
      });
      saveRow(buildPayload(movedEntry));
      savePrefinished(remainingAbout);
      return;
    }

    // Correcting to an abandoned entrant's sail is not allowed (old screen
    // disabled all actions on abandoned rows) — reject and revert like the
    // duplicate case above; don't fall through to the unknown-sail save.
    const abandonedMatch = unfinishedRef.current.find(
      (r) => r.isAbandon && sailMatches(r.sailNum, newSailNum, alpha),
    );
    if (abandonedMatch) {
      propsRef.current.activateSnackbarTab?.(
        `Sail Number ${newSailNum} is abandoned!`,
        "error",
        true,
      );
      return;
    }

    // A TYPED (invalid) About-board box with this sail: the finished row now
    // owns it → consume that box (last-added when several share the sail) so
    // the board and the grid never show the same sail twice. The unknown-sail
    // rename below proceeds regardless.
    boardRef.current?.consumeTypedSail?.(newSailNum, alpha);

    const updatedRow = { ...row, sailNum: newSailNum, oldSailNum: row.sailNum };
    const updated = current.map((r) => (r.id === rowId ? updatedRow : r));
    finishedRef.current = updated;
    dispatch(actions.updateCompleteFinishRaceEntrants(updated));
    saveRow(buildPayload(updatedRow));
  }, []);

  const handlePlaceChange = useCallback((rowId, place) => {
    // position (insertion #) stays independent of place — old-screen parity.
    // A valid place completes the row (clears the review highlight); clearing
    // to "" is only forwarded here when a DN is set (PlaceRenderer reverts the
    // no-DN case), so the row stays complete either way.
    const updated = finishedRef.current.map((r) =>
      r.id === rowId
        ? { ...r, place, ...(place !== "" ? { editable: false } : {}) }
        : r,
    );
    finishedRef.current = updated;
    dispatch(actions.updateCompleteFinishRaceEntrants(updated));
    const row = updated.find((r) => r.id === rowId);
    if (row) {
      const payload = buildPayload(row);
      payload.oldSailNum = null; // place edits never rename — null so backend looks up by sailNum/sessionEntrantId
      saveRow(payload);
    }
  }, []);

  const handleDateChange = useCallback((rowId, value) => {
    const p = propsRef.current;
    const updated = finishedRef.current.map((r) => {
      if (r.id !== rowId) return r;
      const next = { ...r, finishDate: value };
      // OPTIMISTIC verdict so the row recolors the moment the date commits —
      // waiting for the save response left the row the wrong color for the
      // whole server round-trip. The server's verdict still lands afterwards
      // and overrides via saveRow's diff-guard if it disagrees.
      const sessionDate = p.raceSessionsDate || "";
      const nonTimeDn = !!(next.dn && !TIME_PENALTIES.includes(next.dn));
      if (value && sessionDate && !nonTimeDn && !next.isAbandon && !isUnknownSail(next)) {
        if (value < sessionDate) {
          next.editable = true; // finished before the race day → invalid
        } else if (value > sessionDate) {
          next.editable = false; // later day → time-of-day can't be before start
        } else {
          // Same day → valid only if the time is not before the fleet start.
          const fleet = (p.fleetDetails || []).find(
            (f) => f.fleet === next.fleetName,
          );
          const startSec = fleet
            ? toSeconds(fleet.hr, fleet.min, fleet.sec)
            : 0;
          next.editable = startSec > 0 && toSeconds(next.hr, next.min, next.sec) < startSec;
        }
      }
      return next;
    });
    finishedRef.current = updated;
    dispatch(actions.updateCompleteFinishRaceEntrants(updated));
    const row = updated.find((r) => r.id === rowId);
    if (row) {
      const payload = buildPayload(row);
      payload.oldSailNum = null; // date edits never rename — null so backend looks up by sailNum/sessionEntrantId
      saveRow(payload);
    }
  }, []);

  // ── Real-time: watchlist helpers ───────────────────────────────────────────
  // Place mode: default Pos for a card = max place already in Finished + 1.
  // Several About cards may share the default; once one is captured (raising
  // the finished max), the NEXT move-in defaults higher.
  const nextDefaultPlace = () =>
    Math.max(0, ...finishedRef.current.map((r) => Number(r.place) || 0)) + 1;

  // Old-screen parity: a boat sent to Finished with no saved position yet
  // gets the highest position currently in the grid + 1 — not the array
  // length, so it stays correct even with gaps or legacy rows.
  const nextPosition = () =>
    Math.max(0, ...finishedRef.current.map((r) => Number(r.position) || 0)) + 1;

  const nowHMS = () => {
    const d = new Date();
    return {
      hr: String(d.getHours()).padStart(2, "0"),
      min: String(d.getMinutes()).padStart(2, "0"),
      sec: String(d.getSeconds()).padStart(2, "0"),
    };
  };

  const savePrefinished = useCallback((aboutList) => {
    const sessionId = propsRef.current.session;
    if (!sessionId || sessionId <= 0) return;
    try {
      const enc = encodeURIComponent(encrypt(JSON.stringify(sessionId)));
      axiosInstance
        .post("api/clubs/FinishTime/prefinish?raceSession=" + enc, {
          finishTimeDTOs0: [],
          finishTimeDTOs1: aboutList,
          finishTimeDTOs2: [],
        })
        .catch(() => {});
    } catch (e) {
      /* noop */
    }
  }, []);

  // Card Pos edits (place mode): persist via Redux + prefinish only — the
  // FinishTime POST happens at capture.
  const handleAboutPlaceChange = useCallback((rowId, place) => {
    const updated = aboutRef.current.map((r) =>
      r.id === rowId ? { ...r, place } : r,
    );
    aboutRef.current = updated;
    dispatch(actions.updateAboutFinishRaceEntrants(updated));
    savePrefinished(updated);
  }, []);

  const addToWatchlist = useCallback((data) => {
    if (data?.isAbandon) return; // abandoned entrants cannot be watched (old screen parity)
    if (!navigator.onLine) {
      propsRef.current.activateSnackbarTab?.("Network error", "error", true);
      return;
    }
    const sailLower = data.sailNum?.toLowerCase();
    // Double-fire guard: silently ignore if the row already left the
    // unfinished list (refs update synchronously).
    if (
      !unfinishedRef.current.some((r) => r.sailNum?.toLowerCase() === sailLower)
    )
      return;
    if (
      aboutRef.current.some((x) => x.sailNum?.toLowerCase() === sailLower) ||
      finishedRef.current.some((x) => x.sailNum?.toLowerCase() === sailLower)
    ) {
      propsRef.current.activateSnackbarTab?.(
        `Sail Number ${data.sailNum} already exists in FINISHED!`,
        "error",
        true,
      );
      return;
    }
    if (boardRef.current?.hasSailNumber?.(data.sailNum)) {
      propsRef.current.activateSnackbarTab?.(
        `Sail Number ${data.sailNum} already exists in ABOUT TO FINISH!`,
        "error",
        true,
      );
      return;
    }
    if (
      aboutRef.current.length +
        (boardRef.current?.getFilledBlankCount?.() || 0) >=
      MAX_WATCHLIST_SLOTS
    ) {
      propsRef.current.activateSnackbarTab?.(
        `About to Finish is full (max ${MAX_WATCHLIST_SLOTS}). Finish or remove one first.`,
        "error",
        true,
      );
      return;
    }

    const remainingUnfinished = unfinishedRef.current.filter(
      (r) => r.sailNum?.toLowerCase() !== sailLower,
    );
    // Stamp insertion order so the board can interleave moved-in vs typed cards.
    // Place mode: prefill the card's Pos with the current default (editable).
    const newAbout = [
      ...aboutRef.current,
      {
        ...data,
        _watchOrder: nextWatchOrder(),
        ...(propsRef.current.place ? { place: nextDefaultPlace() } : {}),
      },
    ];
    unfinishedRef.current = remainingUnfinished;
    aboutRef.current = newAbout;

    requestAnimationFrame(() => {
      unstable_batchedUpdates(() => {
        dispatch(actions.updateInitialFinishRaceEntrants(remainingUnfinished));
        dispatch(actions.updateAboutFinishRaceEntrants(newAbout));
      });
    });
    savePrefinished(newAbout);
  }, []);

  const captureNow = useCallback((data) => {
    if (data?.isAbandon) return; // abandoned entrants cannot capture a finish (old screen parity)
    if (!navigator.onLine) {
      propsRef.current.activateSnackbarTab?.("Network error", "error", true);
      return;
    }
    const sailLower = data.sailNum?.toLowerCase();
    const { hr, min, sec } = nowHMS();
    const finished = finishedRef.current;

    // A rapid double-tap fires twice — on the second call the sail is already
    // gone from BOTH source lists (refs update synchronously), so bail instead
    // of adding a duplicate finish. Both lists must be checked: an
    // About-to-Finish capture (green board button) targets a row in aboutRef,
    // a Finish-button/typed capture targets a row in unfinishedRef.
    if (
      !unfinishedRef.current.some(
        (r) => r.sailNum?.toLowerCase() === sailLower,
      ) &&
      !aboutRef.current.some((r) => r.sailNum?.toLowerCase() === sailLower)
    )
      return;
    // Place mode: no times — the result is the card's Pos (or the running
    // default). Time mode: capture the wall-clock time as before.
    const isPlaceMode = !!propsRef.current.place;
    const newEntry = {
      ...data,
      hr: isPlaceMode ? "00" : hr,
      min: isPlaceMode ? "00" : min,
      sec: isPlaceMode ? "00" : sec,
      dn: data.dn || "",
      position: nextPosition(),
      place: isPlaceMode
        ? Number(data.place) > 0
          ? Number(data.place)
          : nextDefaultPlace()
        : nextPosition(),
      editable: false,
      finishDate:
        !isPlaceMode && propsRef.current.overnight
          ? new Date().toISOString().split("T")[0]
          : "",
      oldSailNum: "",
    };
    const remainingUnfinished = unfinishedRef.current.filter(
      (r) => r.sailNum?.toLowerCase() !== sailLower,
    );
    const remainingAbout = aboutRef.current.filter(
      (r) => r.sailNum?.toLowerCase() !== sailLower,
    );
    const newFinished = [newEntry, ...finished];
    unfinishedRef.current = remainingUnfinished;
    aboutRef.current = remainingAbout;
    finishedRef.current = newFinished;

    requestAnimationFrame(() => {
      unstable_batchedUpdates(() => {
        dispatch(actions.updateInitialFinishRaceEntrants(remainingUnfinished));
        dispatch(actions.updateAboutFinishRaceEntrants(remainingAbout));
        dispatch(actions.updateCompleteFinishRaceEntrants(newFinished));
      });
      // Dispatches flushed synchronously above → scroll the new top row into view.
      finishedGridRef.current?.api?.ensureIndexVisible(0, "top");
    });
    saveRow(buildPayload(newEntry));
    savePrefinished(remainingAbout);
  }, []);

  const removeFromWatchlist = useCallback((data) => {
    if (!navigator.onLine) {
      propsRef.current.activateSnackbarTab?.("Network error", "error", true);
      return;
    }
    const sailLower = data.sailNum?.toLowerCase();
    // Double-fire guard: already returned to unfinished → nothing to do.
    if (!aboutRef.current.some((r) => r.sailNum?.toLowerCase() === sailLower))
      return;
    const restored = {
      ...data,
      hr: "00",
      min: "00",
      sec: "00",
      dn: "",
      place: "",
      position: "",
      finishDate: "",
      editable: false,
      errorHR: false,
      errorMin: false,
      errorSec: false,
    };
    const remainingAbout = aboutRef.current.filter(
      (r) => r.sailNum?.toLowerCase() !== sailLower,
    );
    const newUnfinished = [...unfinishedRef.current, restored];
    aboutRef.current = remainingAbout;
    unfinishedRef.current = newUnfinished;

    requestAnimationFrame(() => {
      unstable_batchedUpdates(() => {
        dispatch(actions.updateAboutFinishRaceEntrants(remainingAbout));
        dispatch(actions.updateInitialFinishRaceEntrants(newUnfinished));
      });
      // Show the grid bottom where the returned boat lands.
      scrollUnfinishedToBottom();
    });
    savePrefinished(remainingAbout);
  }, [scrollUnfinishedToBottom]);

  const makePhantomRow = useCallback((sailNum, hr, min, sec, typedPlace) => {
    const isPlaceMode = !!propsRef.current.place;
    return {
      id: newPhantomId(),
      sailNum,
      boatName: "",
      fleetName: "",
      hr: isPlaceMode ? "00" : hr,
      min: isPlaceMode ? "00" : min,
      sec: isPlaceMode ? "00" : sec,
      dn: "",
      position: nextPosition(),
      place: isPlaceMode ? typedPlace || nextDefaultPlace() : nextPosition(),
      editable: true,
      finishDate:
        !isPlaceMode && propsRef.current.overnight
          ? new Date().toISOString().split("T")[0]
          : "",
      oldSailNum: "",
    };
  }, []);

  const captureTypedSail = useCallback(
    (typedInput, pos) => {
      // Uppercase at the source (old screen's valueParser) so the phantom row,
      // payload, and exports all carry capitals; matching is case-insensitive.
      // `pos` (place mode only): the typed box's Pos value.
      const typed = (typedInput || "").trim().toUpperCase();
      if (!typed) return false;
      const isPlaceMode = !!propsRef.current.place;
      const posNum = parseInt(pos, 10);
      const typedPlace = !isNaN(posNum) && posNum > 0 ? posNum : null;
      if (!navigator.onLine) {
        propsRef.current.activateSnackbarTab?.("Network error", "error", true);
        return false;
      }
      // SN Alpha rule (old screen): toggle ON → exact full-sail match only;
      // OFF → letters stripped from both sides before comparing.
      const alpha = propsRef.current.snWithAlpha !== false;

      const already =
        finishedRef.current.some((r) =>
          sailMatches(r.sailNum, typed, alpha),
        ) ||
        aboutRef.current.some((r) => sailMatches(r.sailNum, typed, alpha));
      if (already) {
        propsRef.current.activateSnackbarTab?.(
          `Sail Number ${typed} already exists in FINISHED!`,
          "error",
          true,
        );
        return false;
      }

      const match = unfinishedRef.current.find((r) =>
        sailMatches(r.sailNum, typed, alpha),
      );
      if (match) {
        // Abandoned entrant: block the capture (don't fall through to a
        // phantom either — that would duplicate the boat).
        if (match.isAbandon) {
          propsRef.current.activateSnackbarTab?.(
            `Sail Number ${typed} is abandoned!`,
            "error",
            true,
          );
          return false;
        }
        // Place mode: the typed Pos overrides the card default for this capture.
        captureNow(
          isPlaceMode && typedPlace ? { ...match, place: typedPlace } : match,
        );
        return true;
      }

      const { hr, min, sec } = nowHMS();
      const finished = finishedRef.current;
      const phantom = makePhantomRow(typed, hr, min, sec, typedPlace);
      const newFinished = [phantom, ...finished];
      finishedRef.current = newFinished;
      requestAnimationFrame(() => {
        dispatch(actions.updateCompleteFinishRaceEntrants(newFinished));
        // Dispatch flushed → scroll the new top row into view.
        finishedGridRef.current?.api?.ensureIndexVisible(0, "top");
      });
      saveRow(buildPayload(phantom));
      // propsRef.current.activateSnackbarTab?.(
      //   `${typed} not in entrant list — added as unknown, correct the sail #`,
      //   "info",
      //   true,
      // );
      return true;
    },
    [captureNow],
  );

  const searchInputRef = useRef(null);
  const searchTextRef = useRef("");
  searchTextRef.current = unfinishedSearch;
  const focusSearchInput = useCallback(() => {
    if (propsRef.current.mode !== "manual") return false;
    const el = searchInputRef.current;
    if (!el) return false;
    const take = () => {
      if (document.activeElement === el) return;
      el.focus();
      el.select?.();
    };
    take();
    const reassert = () => {
      const ae = document.activeElement;
      const lost =
        !ae ||
        ae === document.body ||
        !ae.isConnected ||
        !!ae.closest?.(".MuiMenu-root, .MuiPopover-root");
      if (lost) take();
    };
    setTimeout(reassert, 180);
    setTimeout(reassert, 550);
    return true;
  }, []);

  const focusFinishedRowEntry = useCallback((rowId) => {
    const tryFocus = () => {
      const api = finishedGridRef.current?.api;
      const idx = api?.getRowNode(String(rowId))?.rowIndex;
      if (idx == null || idx < 0) return false;
      api.ensureIndexVisible(idx, "top");
      const rowSel = `.nft-finished-grid .ag-row[row-index="${idx}"]`;
      const target = propsRef.current.place
        ? document.querySelector(`${rowSel} .nft-place-input input`)
        : document.querySelector(`${rowSel} .nft-time-btn`);
      target?.focus();
      return !!target;
    };
    requestAnimationFrame(() => {
      const landed = tryFocus();
      setTimeout(() => {
        const ae = document.activeElement;
        if (!landed || !ae || ae === document.body || !ae.isConnected) tryFocus();
      }, 90);
    });
  }, []);

  const handleSearchEnter = useCallback(() => {
    if (propsRef.current.mode !== "manual") return;
    const typed = (searchTextRef.current || "").trim();
    if (!typed) return;
    const alpha = propsRef.current.snWithAlpha !== false;
    const pool = [...unfinishedRef.current, ...aboutRef.current].filter(
      (r) => !r.isAbandon,
    );

    let match = pool.find((r) => sailMatches(r.sailNum, typed, alpha));
    if (!match) {
      const partial = pool.filter((r) =>
        sailPartialMatches(r.sailNum, typed, alpha),
      );
      if (partial.length > 1) return;
      match = partial[0];
    }

    if (match) {
      handleUnfinishedClick(match);
      setUnfinishedSearch("");
      focusFinishedRowEntry(match.id); 
      return;
    }

    const alreadyFinished = finishedRef.current.some(
      (r) =>
        sailMatches(r.sailNum, typed, alpha) ||
        sailPartialMatches(r.sailNum, typed, alpha),
    );
    if (alreadyFinished) {
      return;
    }

    if (!navigator.onLine) {
      propsRef.current.activateSnackbarTab?.("Network error", "error", true);
      return;
    }
    const phantom = makePhantomRow(typed.toUpperCase(), "00", "00", "00", null);
    finishedRef.current = [phantom, ...finishedRef.current];
    requestAnimationFrame(() => {
      dispatch(actions.updateCompleteFinishRaceEntrants(finishedRef.current));
    });
    saveRow(buildPayload(phantom));
    propsRef.current.activateSnackbarTab?.("No boat found", "warning", true);
    setUnfinishedSearch("");
    focusFinishedRowEntry(phantom.id);
  }, [focusFinishedRowEntry, makePhantomRow]);

  // Red "needs review" rows via rowClassRules — NOT getRowStyle. AG re-applies
  // getRowStyle only when a row is REDRAWN (so an editable flip from a save
  // verdict didn't recolor until focus moved / the row redrew); rowClassRules
  // are re-evaluated on every data refresh → the row turns red/white the
  // moment the verdict lands. Styling lives in finish-times-new.scss
  // (.nft-error-row). Rules unchanged: unknown-sail rows never red (sail-only
  // highlight), non-time penalties / abandoned rows never red.
  const finishedRowClassRules = useMemo(
    () => ({
      "nft-error-row": (params) => {
        const data = params?.data;
        if (!data) return false;
        if (isUnknownSail(data)) return false;
        const isTimeReadonly =
          !!(data.dn && !TIME_PENALTIES.includes(data.dn)) || !!data.isAbandon;
        return !isTimeReadonly && (data.editable === true || data.editable === 1);
      },
    }),
    [],
  );

  const finishedDefaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      unSortIcon: true,
    }),
    [],
  );
  const unfinishedDefaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: false,
      suppressMovable: true,
      unSortIcon: true,
    }),
    [],
  );

  // ── Grid context (changes only on UI toggles, not on data) ─────────────────
  const penaltiesKey = (props.penalties || [])
    .map((p) => p.penaltyId || p.penaltyName)
    .join(",");
  const gridContext = useMemo(
    () => ({
      snWithAlpha,
      mode,
      compact768: isCompact768,
      compactTablet,
      overnight: !!props.overnight,
      penalties: props.penalties || [],
       fleetDetails: props.fleetDetails || [],
      onTimeBlur: handleTimeBlur,
      onPenaltyChange: handlePenaltyChange,
      onDeleteFinished: handleDeleteFinished,
      onUnfinishedClick: handleUnfinishedClick,
      onAddToWatchlist: addToWatchlist,
      onCaptureNow: captureNow,
      onSailNumSave: handleSailNumSave,
      onPlaceChange: handlePlaceChange,
      onDateChange: handleDateChange,
      onEntryCommitDone: focusSearchInput,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
     [snWithAlpha, mode, penaltiesKey, isCompact768, compactTablet, props.overnight, props.fleetDetails],
  );

  // ── External filter ────────────────────────────────────────────────────────
  const isExternalFilterPresent = useCallback(
    () => unfinishedSearch.trim() !== "",
    [unfinishedSearch],
  );
  const doesFilterPass = useCallback(
    (node) => {
      if (!unfinishedSearch.trim()) return true;
      const q = unfinishedSearch.toLowerCase();
      return (
        node.data?.sailNum?.toLowerCase().includes(q) ||
        node.data?.boatName?.toLowerCase().includes(q)
      );
    },
    [unfinishedSearch],
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Controls toolbar: About to Finish header + On Water / SN Alpha / Mode / Process Results */}
      <ControlsToolbar
        aboutCountRef={aboutCountValueRef}
        aboutCountStoreRef={aboutCountStoreRef}
        aboutAddRef={aboutAddRef}
        onAddBlankBox={() => boardRef.current?.addBlankBox()}
        onWater={props.onWater}
        setOnWater={props.setOnWater}
        snWithAlpha={snWithAlpha}
        setSnWithAlpha={props.setSnWithAlpha}
        currentMode={props.currentMode}
        setMode={props.setMode}
        isPlace={props.isPlace}
        processFlag={props.processFlag}
        noEntries={
          completeFinishRaceEntrants.length === 0 &&
          unfinishedEntrants.length === 0 &&
          aboutFinishRaceEntrants.length === 0
        }
        onProcessResults={props.onProcessResults}
      />

      {/* About to Finish watchlist cards (shown in real-time, hidden in manual).
          Kept MOUNTED across the Real-time/Manual toggle (display:none in manual)
          so locally-typed boxes are retained on tab switches. The key includes
          session/event/date — and NOT mode — so the board still remounts (clearing
          all empty/typed boxes) on a session/date/event change, but never on a
          mode switch. */}
      <Box sx={{ display: mode === "real-time" ? "block" : "none" }}>
        <AboutToFinishBoard
          key={`atf-${props.session}-${props.event}-${props.raceSessionsDate}`}
          ref={boardRef}
          entries={boardEntries}
          snWithAlpha={snWithAlpha}
          isPlace={!!props.place}
          onRemove={removeFromWatchlist}
          onCaptureNow={captureNow}
          onCaptureTyped={captureTypedSail}
          onEntryPlaceChange={handleAboutPlaceChange}
          onCountChange={handleAboutCount}
        />
      </Box>

      <Grid container spacing={compactTablet ? 1 : 2}>
        {/* FINISHED panel — wider (8/12) for overnight so its extra DATE column
            fits without a scroll; the standard 7/12 otherwise. */}
        <Grid
          item
          xs={12}
          sm={props.overnight ? 7.5 : 7}
          md={props.overnight ? 7.5 : 7}
          lg={props.overnight ? 7.5 : 7}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
              // NOWRAP: with wrap enabled the browser dropped the whole
              // search + Retain group onto a second line the moment it did not
              // fit, instead of narrowing the field. Forbidding the wrap makes
              // the search shrink (it is the only flexible item here — the
              // title and the switch are both flexShrink 0).
              flexWrap: "nowrap",
              gap: 0.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "var(--ty-primary-color, #356dad)",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 1,
                display: "flex",
                alignItems: "center",
                lineHeight: 1.2,
                marginBottom: "1px",
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              Finished <CountBadge count={completeFinishRaceEntrants.length} />
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: compactTablet ? 1 : 2,
                ml: "auto",
                minWidth: 0,
                flexShrink: 1,
              }}
            >
              <TextField
                size="small"
                placeholder={
                  props.caseOTBEntrant
                    ? "Search Sail No & Class"
                    : "Search Sail No & Boat"
                }
                className="nft-grid-search"
                value={unfinishedSearch}
                onChange={(e) => setUnfinishedSearch(e.target.value)}
                inputRef={searchInputRef}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearchEnter();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />
                    </InputAdornment>
                  ),
                  endAdornment: unfinishedSearch ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setUnfinishedSearch("")}
                        sx={{ p: 0.25 }}
                        aria-label="Clear search"
                      >
                        <ClearIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: { height: 23, fontSize: 12 },
                }}
                sx={{
                  flex: "1 1 auto",
                  width: "100%",
                  minWidth: 0,
                  maxWidth: 200,
                  "& .MuiOutlinedInput-root": { pl: "8px", pr: "6px" },
                  "& .MuiInputAdornment-positionStart": { mr: "4px" },
                  "& .MuiInputAdornment-positionEnd": { ml: 0 },
                  "& .MuiOutlinedInput-input": { padding: "2px 0", minWidth: 0 },
                }}
              />
              {mode === "manual" && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {!props.place && (
                    <LabeledSwitch
                      label={`Retain HR${retainHr && selectedHr ? ` (${selectedHr})` : ""}`}
                      checked={retainHr}
                      onChange={updateRetainHr}
                    />
                  )}
                  {/* Retain Did Not — hidden for now (feedback after regression).
                      The retain logic below/above is left intact so the switch can
                      be brought back by un-commenting this block.
                  <LabeledSwitch
                    label={`Retain Did Not${retainDidNot && selectedDidNot ? ` (${selectedDidNot})` : ""}`}
                    checked={retainDidNot}
                    onChange={updateRetainDidNot}
                  />
                  */}
                </Box>
              )}
            </Box>
          </Box>
          <Box
            ref={gridWrapRef}
            className="ag-theme-alpine finish-times-grid nft-finished-grid"
            sx={{
              height: isMobile ? 380 : gridHeight,
              overflow: "hidden",
              contain: "content",
              borderRadius: "0.5rem",
            }}
          >
            <AgGridReact
              ref={finishedGridRef}
              rowBuffer={10}
              rowData={completeFinishRaceEntrants}
              columnDefs={finishedColDefs}
              context={gridContext}
              getRowId={getGridRowId}
              getRowStyle={getRowStyle}
              rowClassRules={finishedRowClassRules}
              // NOTE: no onModelUpdated position refresh — "#" renders real
              // data.position, so delta updates re-render exactly the rows
              // whose position changed (a force refresh of the whole column
              // on every update was a main-thread stall on large sessions).
              // rowHeight={compactTablet ? 32 : 40}
              // headerHeight={compactTablet ? 30 : 36}
              rowHeight={compactTablet ? 32 : 34}
              headerHeight={24}
              animateRows={false}
              suppressScrollOnNewData
              suppressCellFocus
              suppressHorizontalScroll={compactTablet}
              domLayout="normal"
              defaultColDef={finishedDefaultColDef}
              isExternalFilterPresent={isExternalFilterPresent}
              doesExternalFilterPass={doesFilterPass}
              overlayNoRowsTemplate="No finished entrants."
            />
          </Box>
        </Grid>

        {/* UNFINISHED panel — narrower (4/12) for overnight to match the wider
            finished panel; standard 5/12 otherwise. */}
        <Grid
          item
          xs={12}
          sm={props.overnight ? 4.5 : 5}
          md={props.overnight ? 4.5 : 5}
          lg={props.overnight ? 4.5 : 5}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
              flexWrap: "nowrap",
              gap: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "var(--ty-primary-color, #356dad)",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 1,
                display: "flex",
                alignItems: "center",
                lineHeight: 1.2,
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              Unfinished <CountBadge count={filteredUnfinished.length} />
            </Typography>
          </Box>
          <Box
            className="ag-theme-alpine finish-times-grid nft-unfinished-grid"
            sx={{
              height: isMobile ? 380 : gridHeight,
              overflow: "hidden",
              contain: "content",
              borderRadius: "0.5rem",
            }}
          >
            <AgGridReact
              ref={unfinishedGridRef}
              rowBuffer={20}
              rowData={filteredUnfinished}
              columnDefs={unfinishedColDefs}
              context={gridContext}
              getRowId={getGridRowId}
              rowHeight={compactTablet ? 32 : 34}
              headerHeight={24}
              animateRows={false}
              suppressScrollOnNewData
              suppressCellFocus
              suppressHorizontalScroll={compactTablet}
              domLayout="normal"
              defaultColDef={unfinishedDefaultColDef}
              isExternalFilterPresent={isExternalFilterPresent}
              doesExternalFilterPass={doesFilterPass}
              getRowStyle={getRowStyle}
              overlayNoRowsTemplate="No unfinished entrants."
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default React.memo(FinishTimeBody);
